import { useState, type CSSProperties } from 'react'
import { AppLogo } from '@/React/Shared/Component/AppLogo'
import { AppLogoSvg } from '@/React/Shared/Component/AppLogoSvg'
import { Icon, IconNameEnum } from '@/React/Shared/Component/Icon'
import { LogoIcon } from '@/React/Shared/Component/LogoIcon'
import { Button, type ButtonColor } from '@/React/Shared/Component/Button'
import { ButtonSquareIcon } from '@/React/Shared/Component/ButtonSquareIcon'
import { Badge, type BadgeColor } from '@/React/Shared/Component/Badge'
import { Alert, type AlertType } from '@/React/Shared/Component/Alert'
import { Banner } from '@/React/Shared/Component/Banner'
import { Card } from '@/React/Shared/Component/Card'
import { SimpleStatCard } from '@/React/Shared/Component/SimpleStatCard'
import { CountryFlag } from '@/React/Shared/Component/CountryFlag'
import { ProgressBar } from '@/React/Shared/Component/ProgressBar'
import { PieChart } from '@/React/Shared/Component/PieChart'
import { BreadCrumbs } from '@/React/Shared/Component/BreadCrumbs'
import { Pagination } from '@/React/Shared/Component/Pagination'
import { Tabs } from '@/React/Shared/Component/Tabs'
import { SideFormSection } from '@/React/Shared/Component/SideFormSection'
import { Tooltip } from '@/React/Shared/Component/Tooltip'
import { Image } from '@/React/Shared/Component/Image'
import { BookingFormTitle } from '@/React/Shared/Component/BookingFormTitle'
import { BaseMockBookingForm } from '@/React/Shared/Component/BaseMockBookingForm'
import { MockBookingFormService } from '@/React/Shared/Component/MockBookingFormService'
import { Table } from '@/React/Shared/Component/Table'
import { PageHeader } from '@/React/Shared/Component/PageHeader'
import { Dropdown } from '@/React/Shared/Component/Dropdown'
import { DropdownItem } from '@/React/Shared/Component/DropdownItem'
import { LocaleDropdown } from '@/React/Shared/Component/LocaleDropdown'
import { ProfileDropdown } from '@/React/Shared/Component/ProfileDropdown'
import { Modal } from '@/React/Shared/Component/Modal'
import { ModalConfirmation } from '@/React/Shared/Component/ModalConfirmation'
import { Drawer } from '@/React/Shared/Component/Drawer'
import { FlashMessage } from '@/React/Shared/Component/Flash/FlashMessage'
import { flashMessageStore } from '@/React/Shared/Component/Flash/FlashMessageStore'
import { Accordion } from '@/React/Shared/Component/Accordion'
import { TipTapEditor } from '@/React/Shared/Component/TipTapEditor'
import { FormControl } from '@/React/Shared/Component/Forms/FormControl'
import { TextInput } from '@/React/Shared/Component/Forms/TextInput'
import { TextareaInput } from '@/React/Shared/Component/Forms/TextareaInput'
import { CheckboxInput } from '@/React/Shared/Component/Forms/CheckboxInput'
import { ColorInput } from '@/React/Shared/Component/Forms/ColorInput'
import { CustomRadioInput } from '@/React/Shared/Component/Forms/CustomRadioInput'
import { RadioInput } from '@/React/Shared/Component/Forms/RadioInput'
import { FileInput } from '@/React/Shared/Component/Forms/FileInput'
import { ImageFileInput } from '@/React/Shared/Component/Forms/ImageFileInput'
import { SelectInput } from '@/React/Shared/Component/Forms/SelectInput'
import {
    SelectSearchInput,
    type SearcheableEntity,
} from '@/React/Shared/Component/Forms/SelectSearchInput'
import { ToggleInput } from '@/React/Shared/Component/Forms/ToggleInput'
import { ApiForm, useApiForm } from '@/React/Shared/Component/Forms/ApiForm'
import { ApiFormButton } from '@/React/Shared/Component/Forms/ApiFormButton'
import { ApiFormControl } from '@/React/Shared/Component/Forms/ApiFormControl'
import { RequestBodyKeyEnum } from '@/Shared/AutoGenerated/RequestFieldEnum'
import {
    LocaleEnum,
    LocaleEnumData,
} from '@/Shared/AutoGenerated/LocalizationEnum'
import User from '@/App/User/Entity/User'
import UserID from '@/App/User/Entity/UserID'
import CompanyUser from '@/App/CompanyUser/Entity/CompanyUser'
import CompanyID from '@/App/Company/Entity/CompanyID'
import CompanyUserID from '@/App/CompanyUser/Entity/CompanyUserID'
import { FlashStatusTypeEnum, UserRoleEnum } from '@/Shared/AutoGenerated/Main'
import { TimeZoneEnum } from '@/Shared/AutoGenerated/TimeZoneEnum'

export function KitchenSinkPage() {
    // Interactive component states
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)
    const [drawerWidth, setDrawerWidth] = useState<'standard' | 'wide'>(
        'standard',
    )
    const [isDrawerLoading, setIsDrawerLoading] = useState(false)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false)
    const [selectedTabKey, setSelectedTabKey] = useState('overview')
    const [currentPage, setCurrentPage] = useState(3)
    const [demoTextInput, setDemoTextInput] = useState('John Doe')
    const [demoPrefixInput, setDemoPrefixInput] = useState('tinylocal')
    const [demoTextarea, setDemoTextarea] = useState(
        'This is sample textarea content for kitchen sink preview.',
    )
    const [demoCheckbox, setDemoCheckbox] = useState(true)
    const [demoCheckboxUnchecked, setDemoCheckboxUnchecked] = useState(false)
    const [demoColor, setDemoColor] = useState('#4f46e5')
    const [demoRadio, setDemoRadio] = useState('daily')
    const [demoCustomRadio, setDemoCustomRadio] = useState('option-b')
    const [demoSelect, setDemoSelect] = useState('es')
    const [demoSelectSearch, setDemoSelectSearch] = useState('utc')
    const [demoToggleNormal, setDemoToggleNormal] = useState(true)
    const [demoToggleLarge, setDemoToggleLarge] = useState(false)
    const apiForm = useApiForm()

    const sampleSearchList: SearcheableEntity[] = [
        {
            id: '1',
            value: 'utc',
            displayText: 'UTC (Coordinated Universal Time)',
        },
        {
            id: '2',
            value: 'est',
            displayText: 'EST (Eastern Standard Time - US/East)',
        },
        {
            id: '3',
            value: 'cst',
            displayText: 'CST (Central Standard Time - US/Central)',
        },
        {
            id: '4',
            value: 'pst',
            displayText: 'PST (Pacific Standard Time - US/Pacific)',
        },
        {
            id: '5',
            value: 'gmt',
            displayText: 'GMT (Greenwich Mean Time - London)',
        },
        {
            id: '6',
            value: 'cet',
            displayText: 'CET (Central European Time - Paris)',
        },
    ]

    // Mock User and CompanyUser for ProfileDropdown
    const mockUser = new User(
        new UserID(1),
        'Isaac',
        'Souza',
        'isaac@example.com',
        null,
        true,
    )
    const mockCompanyUser = new CompanyUser(
        new CompanyUserID(1),
        new CompanyID(1),
        new UserID(1),
        UserRoleEnum.Owner,
        TimeZoneEnum.America_Fortaleza,
        true,
        false,
    )

    const buttonColors: ButtonColor[] = [
        'primary',
        'dark',
        'light',
        'info',
        'warning',
        'danger',
        'success',
        'outline',
        'dark-outline',
        'danger-outline',
        'info-outline',
    ]

    const badgeColors: BadgeColor[] = [
        'success',
        'success-ghost',
        'info',
        'info-ghost',
        'warning',
        'warning-ghost',
        'danger',
        'danger-ghost',
        'secondary',
        'secondary-ghost',
    ]

    const alertTypes: AlertType[] = ['success', 'info', 'warning', 'danger']

    const mockSearchResults = {
        numberOfPages: 5,
        currentPage,
        perPage: 10,
        numberOfRecords: 50,
        records: [],
        beforeCurrent: [1, 2].filter((p) => p < currentPage),
        afterCurrent: [4, 5].filter((p) => p > currentPage),
    }

    const mockTabs = [
        {
            key: 'overview',
            title: 'Overview',
            url: '#overview',
            iconName: IconNameEnum.Home,
            iconClass: 'w-4 h-4',
        },
        {
            key: 'settings',
            title: 'Settings',
            url: '#settings',
            iconName: IconNameEnum.Gears,
            iconClass: 'w-4 h-4',
        },
        {
            key: 'users',
            title: 'Team Members',
            url: '#users',
            iconName: IconNameEnum.Company,
            iconClass: 'w-4 h-4',
        },
    ]

    return (
        <div className="min-h-screen bg-gray-50 text-gray-800">
            <div className="max-w-6xl mx-auto p-6 md:p-10 space-y-12">
                {/* Flash message container */}
                <FlashMessage />

                <div className="border-b pb-4">
                    <h1 className="text-3xl font-extrabold text-gray-900">
                        React Components Kitchen Sink
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Complete demonstration of all ported React components
                        with variations and interactive states.
                    </p>
                </div>

                {/* 1. AppLogo & AppLogoSvg */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        1. AppLogo & AppLogoSvg
                    </h2>
                    <div className="flex items-center gap-8 bg-white p-4 rounded-xl border border-stone-200">
                        <div>
                            <div className="text-xs text-gray-500 mb-2">
                                AppLogo
                            </div>
                            <AppLogo appName="TinyCal" />
                        </div>
                        <div>
                            <div className="text-xs text-gray-500 mb-2">
                                AppLogoSvg
                            </div>
                            <AppLogoSvg className="h-8 w-auto text-primary" />
                        </div>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 2. Icon & LogoIcon */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        2. Icon & LogoIcon
                    </h2>
                    <div className="bg-white p-4 rounded-xl border border-stone-200 space-y-4">
                        <div className="text-xs text-gray-500">
                            Sample Icons
                        </div>
                        <div className="flex flex-wrap gap-4 items-center text-gray-700">
                            <Icon
                                name={IconNameEnum.CircleCheck}
                                className="w-6 h-6 text-green-600"
                            />
                            <Icon
                                name={IconNameEnum.Alert}
                                className="w-6 h-6 text-yellow-500"
                            />
                            <Icon
                                name={IconNameEnum.Close}
                                className="w-6 h-6 text-red-500"
                            />
                            <Icon
                                name={IconNameEnum.Calendar}
                                className="w-6 h-6 text-blue-500"
                            />
                            <Icon
                                name={IconNameEnum.Clock}
                                className="w-6 h-6 text-gray-600"
                            />
                            <Icon
                                name={IconNameEnum.Edit}
                                className="w-6 h-6 text-gray-600"
                            />
                            <Icon
                                name={IconNameEnum.Trash}
                                className="w-6 h-6 text-gray-600"
                            />
                            <Icon
                                name={IconNameEnum.RotateLeft}
                                className="w-6 h-6 text-gray-600"
                            />
                            <Icon
                                name={IconNameEnum.RotateRight}
                                className="w-6 h-6 text-gray-600"
                            />
                            <Icon
                                name={IconNameEnum.Bolt}
                                className="w-6 h-6 text-amber-500"
                            />
                        </div>
                        <div className="text-xs text-gray-500 pt-2">
                            LogoIcon
                        </div>
                        <LogoIcon className="w-10 h-10 text-primary" />
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 3. Button & ButtonSquareIcon */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        3. Button & ButtonSquareIcon
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200 space-y-6">
                        <div>
                            <div className="text-xs font-semibold text-gray-500 uppercase mb-3">
                                Button Color Variations
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {buttonColors.map((color) => (
                                    <Button
                                        key={color}
                                        color={color}
                                    >
                                        {color}
                                    </Button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div className="text-xs font-semibold text-gray-500 uppercase mb-3">
                                Button Sizes & States
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                                <Button
                                    size="small"
                                    color="primary"
                                >
                                    Small Button
                                </Button>
                                <Button
                                    size="normal"
                                    color="primary"
                                >
                                    Normal Button
                                </Button>
                                <Button
                                    disabled
                                    color="primary"
                                >
                                    Disabled
                                </Button>
                                <Button
                                    loading
                                    color="primary"
                                >
                                    Loading
                                </Button>
                                <Button
                                    iconName={IconNameEnum.Calendar}
                                    color="dark"
                                >
                                    With Icon Left
                                </Button>
                                <Button
                                    iconName={IconNameEnum.ArrowRight}
                                    iconRight
                                    color="light"
                                >
                                    With Icon Right
                                </Button>
                                <Button
                                    as="a"
                                    href="#demo"
                                    color="outline"
                                >
                                    As Anchor Tag
                                </Button>
                            </div>
                        </div>

                        <div>
                            <div className="text-xs font-semibold text-gray-500 uppercase mb-3">
                                ButtonSquareIcon
                            </div>
                            <div className="flex items-center gap-3">
                                <ButtonSquareIcon aria-label="Close">
                                    <Icon
                                        name={IconNameEnum.Close}
                                        className="w-5 h-5"
                                    />
                                </ButtonSquareIcon>
                                <ButtonSquareIcon aria-label="Edit">
                                    <Icon
                                        name={IconNameEnum.Edit}
                                        className="w-5 h-5"
                                    />
                                </ButtonSquareIcon>
                                <ButtonSquareIcon aria-label="Delete">
                                    <Icon
                                        name={IconNameEnum.Trash}
                                        className="w-5 h-5"
                                    />
                                </ButtonSquareIcon>
                            </div>
                        </div>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 4. Badge */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        4. Badge
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200 space-y-4">
                        <div>
                            <div className="text-xs font-semibold text-gray-500 uppercase mb-3">
                                Badge Color Variations (Small)
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {badgeColors.map((color) => (
                                    <Badge
                                        key={color}
                                        color={color}
                                        size="small"
                                    >
                                        {color}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div className="text-xs font-semibold text-gray-500 uppercase mb-3">
                                Badge Color Variations (Medium)
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {badgeColors.map((color) => (
                                    <Badge
                                        key={color}
                                        color={color}
                                        size="medium"
                                    >
                                        {color}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 5. Alert */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        5. Alert
                    </h2>
                    <div className="space-y-3">
                        {alertTypes.map((type) => (
                            <Alert
                                key={type}
                                type={type}
                            >
                                <Alert.Title>{`${type.charAt(0).toUpperCase() + type.slice(1)} Alert`}</Alert.Title>
                                <Alert.Content>
                                    This is an example of a {type} notification
                                    message rendered using the Alert component.
                                </Alert.Content>
                            </Alert>
                        ))}
                        <Alert.Container type="success">
                            <Alert.Title>
                                Operation Completed Successfully
                            </Alert.Title>
                            <Alert.Content>
                                Your changes have been saved successfully.
                            </Alert.Content>
                        </Alert.Container>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 6. Banner */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        6. Banner
                    </h2>
                    <div className="rounded-xl overflow-hidden space-y-2">
                        <Banner>
                            <Banner.Icon iconName={IconNameEnum.Alert} />
                            <Banner.Title>Maintenance Notice:</Banner.Title>
                            <Banner.Message>
                                Scheduled maintenance is planned for tonight at
                                midnight UTC.
                            </Banner.Message>
                        </Banner>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 7. Card & CardTitle */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        7. Card & CardTitle
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card.Container>
                            <Card.Title iconName={IconNameEnum.CreditCard}>
                                Standard Card
                            </Card.Title>
                            <Card.Content>
                                <p className="text-sm text-gray-600">
                                    This card uses Card.Header, Card.Body, and
                                    Card.Footer.
                                </p>
                            </Card.Content>
                            <Card.Actions>
                                <Button
                                    size="small"
                                    color="primary"
                                >
                                    Learn More
                                </Button>
                            </Card.Actions>
                        </Card.Container>

                        <Card.Container isActive>
                            <Card.Title iconName={IconNameEnum.CircleCheck}>
                                Active / Selected Card
                            </Card.Title>
                            <Card.Content>
                                <p className="text-sm text-gray-600">
                                    Highlighted with active primary border and
                                    subtle background tint.
                                </p>
                            </Card.Content>
                            <Card.Actions>
                                <Button
                                    size="small"
                                    color="dark"
                                >
                                    Active State
                                </Button>
                            </Card.Actions>
                        </Card.Container>

                        <Card.Container
                            shouldHighlightOnHover
                            isHighlighted
                        >
                            <Card.Title iconName={IconNameEnum.Bolt}>
                                Highlighted / Hoverable
                            </Card.Title>
                            <Card.Content>
                                <p className="text-sm text-gray-600">
                                    Uses ring highlight on hover or permanent
                                    highlight.
                                </p>
                            </Card.Content>
                            <Card.Actions>
                                <Button
                                    size="small"
                                    color="light"
                                >
                                    Details
                                </Button>
                            </Card.Actions>
                        </Card.Container>
                    </div>
                    <div className="bg-white p-4 rounded-xl border border-stone-200">
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                            Standalone CardTitle Component
                        </div>
                        <Card.Title iconName={IconNameEnum.Calendar}>
                            Standalone Card Title
                        </Card.Title>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 8. SimpleStatCard */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        8. SimpleStatCard
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <SimpleStatCard
                            title="Total Bookings"
                            value="1,248"
                        />
                        <SimpleStatCard
                            title="Active Services"
                            value="14"
                        />
                        <SimpleStatCard
                            title="Conversion Rate"
                            value="68%"
                        />
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 9. CountryFlag */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        9. CountryFlag (All 8 Locales)
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200 grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {Object.values(LocaleEnum).map((locale) => (
                            <div
                                key={locale}
                                className="flex items-center gap-3 p-3 rounded-lg border border-stone-100 bg-stone-50"
                            >
                                <CountryFlag
                                    locale={locale}
                                    className="w-6 h-6 shrink-0 shadow-xs"
                                />
                                <div className="text-xs">
                                    <div className="font-semibold text-gray-800">
                                        {
                                            LocaleEnumData[locale]
                                                .nativeLanguageLabel
                                        }
                                    </div>
                                    <div className="text-gray-500">
                                        {locale}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 10. ProgressBar */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        10. ProgressBar
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200 space-y-4">
                        <div>
                            <div className="text-xs text-gray-500 mb-1">
                                25% Progress
                            </div>
                            <ProgressBar percentage={25} />
                        </div>
                        <div>
                            <div className="text-xs text-gray-500 mb-1">
                                50% Progress
                            </div>
                            <ProgressBar percentage={50} />
                        </div>
                        <div>
                            <div className="text-xs text-gray-500 mb-1">
                                75% Progress
                            </div>
                            <ProgressBar percentage={75} />
                        </div>
                        <div>
                            <div className="text-xs text-gray-500 mb-1">
                                100% Progress
                            </div>
                            <ProgressBar percentage={100} />
                        </div>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 11. PieChart */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        11. PieChart
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200 flex items-center justify-around">
                        <div className="text-center">
                            <PieChart data={[10, 20, 40, 50]} />
                            <div className="text-xs text-gray-500 mt-2">
                                Default [10, 20, 40, 50]
                            </div>
                        </div>
                        <div className="text-center">
                            <PieChart
                                data={[30, 30, 40]}
                                colors={['#3b82f6', '#10b981', '#f59e0b']}
                            />
                            <div className="text-xs text-gray-500 mt-2">
                                Custom Data & Colors
                            </div>
                        </div>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 12. BreadCrumbs */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        12. BreadCrumbs
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200 space-y-4">
                        <BreadCrumbs>
                            <BreadCrumbs.Item url={'#home'}>
                                Home
                            </BreadCrumbs.Item>
                            <BreadCrumbs.Separator />
                            <BreadCrumbs.Item url={'#settings'}>
                                Settings
                            </BreadCrumbs.Item>
                            <BreadCrumbs.Separator />
                            <BreadCrumbs.Item url={'#services'}>
                                Services
                            </BreadCrumbs.Item>
                            <BreadCrumbs.Separator />
                            <BreadCrumbs.Item isCurrent>
                                Consultation Call
                            </BreadCrumbs.Item>
                        </BreadCrumbs>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 13. Pagination */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        13. Pagination
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200">
                        <div className="text-xs text-gray-500 text-center mb-2">
                            Interactive Page Switcher (Current: {currentPage})
                        </div>
                        <Pagination
                            searchResults={mockSearchResults}
                            as="button"
                            onClick={({ page }) => setCurrentPage(page)}
                        />
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 14. Tabs */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        14. Tabs
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200">
                        <Tabs>
                            <Tabs.List>
                                {mockTabs.map((tab) => (
                                    <Tabs.Item
                                        key={tab.key}
                                        href={tab.url}
                                        isSelected={selectedTabKey === tab.key}
                                        iconName={tab.iconName}
                                        iconClass={tab.iconClass}
                                        onClick={() =>
                                            setSelectedTabKey(tab.key)
                                        }
                                    >
                                        {tab.title}
                                    </Tabs.Item>
                                ))}
                            </Tabs.List>
                        </Tabs>
                        <div className="pt-4 text-sm text-gray-600">
                            Active tab content for:{' '}
                            <strong>{selectedTabKey}</strong>
                        </div>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 15. SideFormSection */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        15. SideFormSection
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200 space-y-6">
                        <SideFormSection>
                            <SideFormSection.Header>
                                <SideFormSection.Title>
                                    General Settings
                                </SideFormSection.Title>
                                <SideFormSection.Subtitle>
                                    Update your general personal and business
                                    profile details.
                                </SideFormSection.Subtitle>
                            </SideFormSection.Header>
                            <SideFormSection.Content>
                                <div className="space-y-2">
                                    <label
                                        htmlFor="kitchen-sink-display-name"
                                        className="block text-sm font-medium text-gray-700"
                                    >
                                        Display Name
                                    </label>
                                    <input
                                        id="kitchen-sink-display-name"
                                        type="text"
                                        defaultValue="Acme Consulting"
                                        className="w-full border rounded-lg p-2 text-sm"
                                    />
                                </div>
                            </SideFormSection.Content>
                        </SideFormSection>
                        <SideFormSection.Divider />
                        <SideFormSection>
                            <SideFormSection.Header>
                                <SideFormSection.Title>
                                    Danger Zone
                                </SideFormSection.Title>
                                <SideFormSection.Subtitle>
                                    Actions that cannot be easily reversed.
                                </SideFormSection.Subtitle>
                            </SideFormSection.Header>
                            <SideFormSection.RawContent>
                                <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                                    <Button
                                        color="danger"
                                        size="small"
                                    >
                                        Delete Account
                                    </Button>
                                </div>
                            </SideFormSection.RawContent>
                        </SideFormSection>
                        <SideFormSection.Divider />
                        <SideFormSection>
                            <SideFormSection.Header>
                                <SideFormSection.Title>
                                    Notification Preferences
                                </SideFormSection.Title>
                                <SideFormSection.Subtitle>
                                    Choose which email updates and reminders you
                                    receive.
                                </SideFormSection.Subtitle>
                            </SideFormSection.Header>
                            <SideFormSection.Content>
                                <div className="space-y-3 text-sm text-gray-700">
                                    <label
                                        htmlFor="kitchen-sink-email-notifications"
                                        className="flex items-center gap-2 cursor-pointer"
                                    >
                                        <input
                                            id="kitchen-sink-email-notifications"
                                            type="checkbox"
                                            defaultChecked
                                            className="rounded text-primary"
                                        />
                                        <span>
                                            Email notifications on new bookings
                                        </span>
                                    </label>
                                    <label
                                        htmlFor="kitchen-sink-weekly-digest"
                                        className="flex items-center gap-2 cursor-pointer"
                                    >
                                        <input
                                            id="kitchen-sink-weekly-digest"
                                            type="checkbox"
                                            defaultChecked
                                            className="rounded text-primary"
                                        />
                                        <span>Weekly schedule digest</span>
                                    </label>
                                </div>
                            </SideFormSection.Content>
                        </SideFormSection>
                        <SideFormSection.Divider />
                        <SideFormSection>
                            <SideFormSection.Header>
                                <SideFormSection.Title>
                                    Raw Layout Subsection
                                </SideFormSection.Title>
                                <SideFormSection.Subtitle>
                                    Renders arbitrary layout without Card
                                    wrapper.
                                </SideFormSection.Subtitle>
                            </SideFormSection.Header>
                            <SideFormSection.RawContent>
                                <div className="border border-dashed border-gray-300 p-4 rounded-xl text-sm text-gray-600">
                                    Directly placed custom component content in
                                    a 8-column span.
                                </div>
                            </SideFormSection.RawContent>
                        </SideFormSection>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 17. Tooltip */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        17. Tooltip
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200 flex gap-6 items-center">
                        <Tooltip
                            text="Tooltip aligned right"
                            align="left"
                        >
                            <Button color="light">Hover me (Right)</Button>
                        </Tooltip>
                        <Tooltip
                            text="Tooltip aligned left"
                            align="right"
                        >
                            <Button color="light">Hover me (Left)</Button>
                        </Tooltip>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 18. Image & BookingFormTitle */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        18. Image & BookingFormTitle
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200 space-y-4">
                        <BookingFormTitle>
                            Select a Date & Time for Your Consultation
                        </BookingFormTitle>
                        <div className="flex items-center gap-4">
                            <Image
                                src="/static/images/profile.png"
                                alt="Sample Profile"
                                className="w-16 h-16 rounded-full object-cover border"
                            />
                            <span className="text-sm text-gray-500">
                                Lazy-loaded responsive Image component
                            </span>
                        </div>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 19. BaseMockBookingForm & MockBookingFormService */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        19. BaseMockBookingForm & MockBookingFormService
                    </h2>
                    <div className="max-w-md">
                        <BaseMockBookingForm>
                            <BaseMockBookingForm.Content>
                                <div className="p-3 space-y-2">
                                    <MockBookingFormService
                                        title="30 Minute Consultation"
                                        description="One-on-one discovery session to discuss your business strategy."
                                        duration={30}
                                    />
                                    <MockBookingFormService
                                        title="Weekly Coaching Call"
                                        description="Recurring strategic guidance sessions."
                                        duration={45}
                                        isRecurring
                                    />
                                </div>
                            </BaseMockBookingForm.Content>
                        </BaseMockBookingForm>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 20. Table */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        20. Table
                    </h2>
                    <div className="space-y-6">
                        <Table.Container>
                            <Table.Head>
                                <Table.HeadCell>Name</Table.HeadCell>
                                <Table.HeadCell>Email</Table.HeadCell>
                                <Table.HeadCell>Role</Table.HeadCell>
                                <Table.HeadCell>Status</Table.HeadCell>
                            </Table.Head>
                            <Table.Body>
                                <Table.Row>
                                    <Table.Cell>Isaac Souza</Table.Cell>
                                    <Table.Cell>isaac@example.com</Table.Cell>
                                    <Table.Cell>Owner</Table.Cell>
                                    <Table.Cell>
                                        <Badge color="success">Active</Badge>
                                    </Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell>Sarah Connor</Table.Cell>
                                    <Table.Cell>sarah@example.com</Table.Cell>
                                    <Table.Cell>Admin</Table.Cell>
                                    <Table.Cell>
                                        <Badge color="info">Invited</Badge>
                                    </Table.Cell>
                                </Table.Row>
                            </Table.Body>
                        </Table.Container>

                        <div>
                            <div className="text-xs text-gray-500 mb-2">
                                Table with EmptyState:
                            </div>
                            <Table.Container
                                isEmpty
                                emptyState={
                                    <Table.EmptyState
                                        title="No team members found"
                                        subtitle="Invite your colleagues to start collaborating."
                                        action={
                                            <Button
                                                size="small"
                                                color="primary"
                                            >
                                                Invite Member
                                            </Button>
                                        }
                                    />
                                }
                            >
                                <Table.Head>
                                    <Table.HeadCell>Name</Table.HeadCell>
                                    <Table.HeadCell>Role</Table.HeadCell>
                                </Table.Head>
                            </Table.Container>
                        </div>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 21. PageHeader */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        21. PageHeader
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200">
                        <PageHeader>
                            <PageHeader.Title>
                                Services Overview
                            </PageHeader.Title>
                            <PageHeader.Action>
                                <Button
                                    color="primary"
                                    size="small"
                                >
                                    + Add Service
                                </Button>
                            </PageHeader.Action>
                        </PageHeader>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 22. Dropdown & DropdownItem */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        22. Dropdown & DropdownItem
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white p-6 rounded-xl border border-stone-200 space-y-4">
                            <span className="text-sm font-medium text-gray-700 block">
                                Options menu:
                            </span>
                            <Dropdown
                                show={isDropdownOpen}
                                onClose={() => setIsDropdownOpen(false)}
                            >
                                <Dropdown.Trigger>
                                    <Button
                                        color="light"
                                        aria-haspopup="true"
                                        aria-expanded={isDropdownOpen}
                                        onClick={() =>
                                            setIsDropdownOpen((prev) => !prev)
                                        }
                                    >
                                        Options Menu ▼
                                    </Button>
                                </Dropdown.Trigger>
                                <Dropdown.Menu>
                                    <div>
                                        <DropdownItem
                                            onClick={() =>
                                                setIsDropdownOpen(false)
                                            }
                                        >
                                            Edit Details
                                        </DropdownItem>
                                        <DropdownItem
                                            onClick={() =>
                                                setIsDropdownOpen(false)
                                            }
                                        >
                                            Duplicate Service
                                        </DropdownItem>
                                        <DropdownItem
                                            onClick={() =>
                                                setIsDropdownOpen(false)
                                            }
                                        >
                                            Archive Service
                                        </DropdownItem>
                                    </div>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                        <div className="bg-white p-6 rounded-xl border border-stone-200 space-y-4">
                            <span className="text-sm font-medium text-gray-700 block">
                                Actions menu:
                            </span>
                            <Dropdown.Container
                                show={isActionsDropdownOpen}
                                onClose={() => setIsActionsDropdownOpen(false)}
                            >
                                <Dropdown.Trigger>
                                    <Button
                                        color="outline"
                                        aria-haspopup="true"
                                        aria-expanded={isActionsDropdownOpen}
                                        onClick={() =>
                                            setIsActionsDropdownOpen(
                                                (prev) => !prev,
                                            )
                                        }
                                    >
                                        Actions ▼
                                    </Button>
                                </Dropdown.Trigger>
                                <Dropdown.Menu>
                                    <Dropdown.Item
                                        onClick={() =>
                                            setIsActionsDropdownOpen(false)
                                        }
                                    >
                                        View Profile
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() =>
                                            setIsActionsDropdownOpen(false)
                                        }
                                    >
                                        Account Settings
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() =>
                                            setIsActionsDropdownOpen(false)
                                        }
                                    >
                                        Log Out
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown.Container>
                        </div>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 23. LocaleDropdown */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        23. LocaleDropdown
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200 flex items-center justify-between">
                        <span className="text-sm text-gray-600">
                            Locale Selector:
                        </span>
                        <LocaleDropdown
                            currentLocaleEnumData={
                                LocaleEnumData[LocaleEnum.English]
                            }
                        />
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 24. ProfileDropdown */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        24. ProfileDropdown
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200 flex items-center justify-between">
                        <span className="text-sm text-gray-600">
                            User Profile Menu:
                        </span>
                        <ProfileDropdown
                            authUser={mockUser}
                            authCompanyUser={mockCompanyUser}
                            impersonating={false}
                        />
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 25. Modal & ModalConfirmation */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        25. Modal & ModalConfirmation
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200 flex flex-wrap gap-4">
                        <Button
                            color="primary"
                            onClick={() => setIsModalOpen(true)}
                        >
                            Open Sample Modal
                        </Button>
                        <Button
                            color="danger"
                            onClick={() => setIsConfirmModalOpen(true)}
                        >
                            Open Confirmation Modal
                        </Button>
                    </div>

                    {/* Modal Component */}
                    <Modal.Container
                        show={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                    >
                        <Modal.Header>
                            <Modal.Title>Sample Modal Title</Modal.Title>
                        </Modal.Header>
                        <Modal.Content>
                            <p className="text-sm text-gray-600">
                                This is a Modal component with responsive
                                teleporting, Esc key listener, backdrop
                                dismissal, and accessible dialog role.
                            </p>
                        </Modal.Content>
                        <Modal.Actions>
                            <>
                                <Button
                                    color="light"
                                    onClick={() => setIsModalOpen(false)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    color="primary"
                                    onClick={() => setIsModalOpen(false)}
                                >
                                    Save Changes
                                </Button>
                            </>
                        </Modal.Actions>
                    </Modal.Container>

                    {/* ModalConfirmation Component */}
                    <ModalConfirmation
                        show={isConfirmModalOpen}
                        title="Delete Service?"
                        color="danger"
                        actionText="Yes, Delete"
                        closeActionText="Cancel"
                        onClose={() => setIsConfirmModalOpen(false)}
                        action={() => setIsConfirmModalOpen(false)}
                    >
                        Are you sure you want to delete this service? This
                        action cannot be undone.
                    </ModalConfirmation>
                </section>

                <SideFormSection.Divider />

                {/* 26. Drawer */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        26. Drawer
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200 flex flex-wrap gap-4">
                        <Button
                            color="primary"
                            onClick={() => {
                                setDrawerWidth('standard')
                                setIsDrawerLoading(false)
                                setIsDrawerOpen(true)
                            }}
                        >
                            Open Standard Drawer
                        </Button>
                        <Button
                            color="dark"
                            onClick={() => {
                                setDrawerWidth('wide')
                                setIsDrawerLoading(false)
                                setIsDrawerOpen(true)
                            }}
                        >
                            Open Wide Drawer
                        </Button>
                        <Button
                            color="warning"
                            onClick={() => {
                                setDrawerWidth('standard')
                                setIsDrawerLoading(true)
                                setIsDrawerOpen(true)
                            }}
                        >
                            Open Loading Drawer
                        </Button>
                    </div>

                    <Drawer.Container
                        show={isDrawerOpen}
                        width={drawerWidth}
                        loading={isDrawerLoading}
                        onClose={() => setIsDrawerOpen(false)}
                    >
                        <Drawer.Header>
                            <Drawer.Title>Slide-over Drawer Panel</Drawer.Title>
                        </Drawer.Header>
                        <Drawer.Content>
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600">
                                    Drawer supporting slide-out panel, loading
                                    overlay, actions footer, and custom title.
                                </p>
                            </div>
                        </Drawer.Content>
                        <Drawer.Actions>
                            <>
                                <Button
                                    color="light"
                                    onClick={() => setIsDrawerOpen(false)}
                                >
                                    Close
                                </Button>
                                <Button
                                    color="primary"
                                    onClick={() => setIsDrawerOpen(false)}
                                >
                                    Submit
                                </Button>
                            </>
                        </Drawer.Actions>
                    </Drawer.Container>
                </section>

                <SideFormSection.Divider />

                {/* 27. FlashMessage Trigger */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        27. FlashMessage
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200 flex gap-4">
                        <Button
                            color="success"
                            onClick={() =>
                                flashMessageStore.display({
                                    type: FlashStatusTypeEnum.Success,
                                    message: 'Meeting successfully booked!',
                                })
                            }
                        >
                            Trigger Success Flash
                        </Button>
                        <Button
                            color="danger"
                            onClick={() =>
                                flashMessageStore.display({
                                    type: FlashStatusTypeEnum.Error,
                                    message: 'An error occurred while saving.',
                                })
                            }
                        >
                            Trigger Error Flash
                        </Button>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 28. Accordion */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        28. Accordion
                    </h2>
                    <div className="space-y-4">
                        <Accordion.Container>
                            <Accordion.Header
                                subtitle="Click to expand or collapse"
                                iconName={IconNameEnum.InfoCircle}
                            >
                                Frequently Asked Questions #1
                            </Accordion.Header>
                            <Accordion.Content>
                                <p className="text-sm text-gray-600">
                                    Expand this section to view frequently asked
                                    questions.
                                </p>
                                <Accordion.Actions>
                                    <Button
                                        size="small"
                                        color="primary"
                                    >
                                        Learn More
                                    </Button>
                                </Accordion.Actions>
                            </Accordion.Content>
                        </Accordion.Container>

                        <Accordion.Container>
                            <Accordion.Header
                                subtitle="Manage payment details"
                                iconName={IconNameEnum.CreditCard}
                            >
                                Billing &amp; Invoicing
                            </Accordion.Header>
                            <Accordion.Content>
                                <p className="text-sm text-gray-600">
                                    Invoices are sent to your account email on
                                    the 1st of every month.
                                </p>
                            </Accordion.Content>
                        </Accordion.Container>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 29. TipTapEditor */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        29. TipTapEditor
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200">
                        <TipTapEditor
                            heightInPixels={200}
                            initialContent="<p>Welcome to <strong>TinyCal</strong>! This is an interactive rich text editor.</p>"
                            mergeTagGroups={[]}
                        />
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 30. Form Controls: FormControl, TextInput, TextareaInput */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        30. Form Controls (FormControl, TextInput,
                        TextareaInput)
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormControl.Container>
                                <FormControl.Label
                                    htmlFor="demo-name"
                                    required
                                >
                                    Full Name
                                </FormControl.Label>
                                <TextInput
                                    id="demo-name"
                                    defaultValue={demoTextInput}
                                    onChange={(e) =>
                                        setDemoTextInput(e.target.value)
                                    }
                                    placeholder="Enter your full name"
                                />
                                <FormControl.HelperText>
                                    Stored value: {demoTextInput}
                                </FormControl.HelperText>
                            </FormControl.Container>

                            <FormControl.Container>
                                <FormControl.Label
                                    htmlFor="demo-subdomain"
                                    required
                                >
                                    Custom Subdomain (with Prefix)
                                </FormControl.Label>
                                <TextInput
                                    id="demo-subdomain"
                                    prefix="https://"
                                    defaultValue={demoPrefixInput}
                                    onChange={(e) =>
                                        setDemoPrefixInput(e.target.value)
                                    }
                                    placeholder="subdomain"
                                />
                                <FormControl.HelperText>
                                    URL: https://{demoPrefixInput}.tinylocal.net
                                </FormControl.HelperText>
                            </FormControl.Container>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormControl.Container>
                                <FormControl.Label htmlFor="demo-disabled">
                                    Disabled Input
                                </FormControl.Label>
                                <TextInput
                                    id="demo-disabled"
                                    defaultValue="Readonly / Disabled value"
                                    disabled
                                />
                            </FormControl.Container>

                            <FormControl.Container>
                                <FormControl.Label
                                    htmlFor="demo-error"
                                    required
                                >
                                    Input with Error
                                </FormControl.Label>
                                <TextInput
                                    id="demo-error"
                                    defaultValue="invalid-email"
                                />
                                <FormControl.Error error="Please enter a valid email address." />
                            </FormControl.Container>
                        </div>

                        <div>
                            <FormControl.Container>
                                <FormControl.Label htmlFor="demo-textarea">
                                    Textarea Description
                                </FormControl.Label>
                                <TextareaInput
                                    id="demo-textarea"
                                    rows={3}
                                    defaultValue={demoTextarea}
                                    onChange={(e) =>
                                        setDemoTextarea(e.target.value)
                                    }
                                    placeholder="Write something..."
                                />
                                <FormControl.HelperText>
                                    Characters: {demoTextarea.length}
                                </FormControl.HelperText>
                            </FormControl.Container>
                        </div>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 31. CheckboxInput & ToggleInput */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        31. CheckboxInput & ToggleInput
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200 space-y-6">
                        <div>
                            <h3 className="text-sm font-semibold text-gray-600 mb-3">
                                Checkboxes
                            </h3>
                            <div className="flex flex-wrap items-center gap-6">
                                <CheckboxInput
                                    label="Subscribe to newsletter"
                                    defaultChecked={demoCheckbox}
                                    onChange={(e) =>
                                        setDemoCheckbox(e.target.checked)
                                    }
                                />
                                <CheckboxInput
                                    label="Unchecked by default"
                                    defaultChecked={demoCheckboxUnchecked}
                                    onChange={(e) =>
                                        setDemoCheckboxUnchecked(
                                            e.target.checked,
                                        )
                                    }
                                />
                                <CheckboxInput
                                    label="Disabled checkbox"
                                    defaultChecked={true}
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="border-t border-stone-200 pt-4">
                            <h3 className="text-sm font-semibold text-gray-600 mb-3">
                                Toggles (Normal & Large)
                            </h3>
                            <div className="flex flex-wrap items-center gap-8">
                                <ToggleInput
                                    label={`Normal Toggle: ${demoToggleNormal ? 'Enabled' : 'Disabled'}`}
                                    size="normal"
                                    defaultChecked={demoToggleNormal}
                                    onChange={(e) =>
                                        setDemoToggleNormal(e.target.checked)
                                    }
                                />
                                <ToggleInput
                                    label={`Large Toggle: ${demoToggleLarge ? 'Active' : 'Inactive'}`}
                                    size="large"
                                    defaultChecked={demoToggleLarge}
                                    onChange={(e) =>
                                        setDemoToggleLarge(e.target.checked)
                                    }
                                />
                                <ToggleInput
                                    label="Disabled Toggle"
                                    size="normal"
                                    defaultChecked={true}
                                    disabled
                                />
                            </div>
                        </div>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 32. RadioInput & CustomRadioInput */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        32. RadioInput & CustomRadioInput
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200 space-y-6">
                        <div>
                            <h3 className="text-sm font-semibold text-gray-600 mb-3">
                                Standard Radio Group (Selected: {demoRadio})
                            </h3>
                            <div className="flex flex-wrap items-center gap-6">
                                <RadioInput
                                    name="demo-frequency"
                                    label="Daily Summary"
                                    value="daily"
                                    defaultChecked={demoRadio === 'daily'}
                                    onChange={() => setDemoRadio('daily')}
                                />
                                <RadioInput
                                    name="demo-frequency"
                                    label="Weekly Digest"
                                    value="weekly"
                                    defaultChecked={demoRadio === 'weekly'}
                                    onChange={() => setDemoRadio('weekly')}
                                />
                                <RadioInput
                                    name="demo-frequency"
                                    label="Monthly Report"
                                    value="monthly"
                                    defaultChecked={demoRadio === 'monthly'}
                                    onChange={() => setDemoRadio('monthly')}
                                />
                                <RadioInput
                                    name="demo-frequency"
                                    label="Disabled Option"
                                    value="never"
                                    defaultChecked={false}
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="border-t border-stone-200 pt-4">
                            <h3 className="text-sm font-semibold text-gray-600 mb-3">
                                Custom Radio (Peer-checked card styles)
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <CustomRadioInput
                                    name="custom-radio-demo"
                                    value="option-a"
                                    defaultChecked={
                                        demoCustomRadio === 'option-a'
                                    }
                                    onChange={() =>
                                        setDemoCustomRadio('option-a')
                                    }
                                >
                                    <div className="font-semibold text-gray-900">
                                        Option A - Standard Delivery
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                        Delivered within 3-5 business days. Free
                                        of charge.
                                    </div>
                                </CustomRadioInput>

                                <CustomRadioInput
                                    name="custom-radio-demo"
                                    value="option-b"
                                    defaultChecked={
                                        demoCustomRadio === 'option-b'
                                    }
                                    onChange={() =>
                                        setDemoCustomRadio('option-b')
                                    }
                                >
                                    <div className="font-semibold text-gray-900">
                                        Option B - Express Priority
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                        Delivered next morning before 10 AM.
                                    </div>
                                </CustomRadioInput>
                            </div>
                        </div>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 33. SelectInput & SelectSearchInput */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        33. SelectInput & SelectSearchInput
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormControl.Container>
                                <FormControl.Label htmlFor="demo-select">
                                    Standard SelectInput
                                </FormControl.Label>
                                <SelectInput
                                    id="demo-select"
                                    defaultValue={demoSelect}
                                    onChange={(e) =>
                                        setDemoSelect(e.target.value)
                                    }
                                >
                                    <option value="en">English (US)</option>
                                    <option value="es">
                                        Spanish (Español)
                                    </option>
                                    <option value="pt">
                                        Portuguese (Português)
                                    </option>
                                    <option value="de">German (Deutsch)</option>
                                    <option value="fr">
                                        French (Français)
                                    </option>
                                </SelectInput>
                                <FormControl.HelperText>
                                    Selected language code: {demoSelect}
                                </FormControl.HelperText>
                            </FormControl.Container>

                            <FormControl.Container>
                                <FormControl.Label id="demo-select-search-label">
                                    Searchable Select (SelectSearchInput)
                                </FormControl.Label>
                                <SelectSearchInput
                                    id="demo-select-search"
                                    aria-labelledby="demo-select-search-label"
                                    list={sampleSearchList}
                                    defaultValue={demoSelectSearch}
                                    onChange={(val) => setDemoSelectSearch(val)}
                                />
                                <FormControl.HelperText>
                                    Selected timezone code: {demoSelectSearch}
                                </FormControl.HelperText>
                            </FormControl.Container>
                        </div>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 34. ColorInput, FileInput & ImageFileInput */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        34. ColorInput, FileInput & ImageFileInput
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormControl.Container>
                                <FormControl.Label htmlFor="demo-color">
                                    Brand Color Picker
                                </FormControl.Label>
                                <div className="flex items-center gap-4">
                                    <div className="w-32">
                                        <ColorInput
                                            id="demo-color"
                                            defaultValue={demoColor}
                                            onChange={(e) =>
                                                setDemoColor(e.target.value)
                                            }
                                        />
                                    </div>
                                    <span className="font-mono text-sm text-gray-700 font-medium">
                                        {demoColor}
                                    </span>
                                    <div
                                        className="w-8 h-8 rounded-lg border border-stone-300 shadow-xs"
                                        style={{ backgroundColor: demoColor }}
                                    />
                                </div>
                            </FormControl.Container>

                            <FormControl.Container>
                                <FormControl.Label htmlFor="demo-file">
                                    Upload Document (Thin FileInput)
                                </FormControl.Label>
                                <FileInput
                                    id="demo-file"
                                    accept=".pdf,.doc,.docx,image/*"
                                    maxFiles={1}
                                    maxSizeInBytes={5 * 1024 * 1024}
                                />
                            </FormControl.Container>

                            <FormControl.Container>
                                <FormControl.Label htmlFor="demo-image-preview-file">
                                    Upload Photo (ImageFileInput)
                                </FormControl.Label>
                                <ImageFileInput
                                    id="demo-image-preview-file"
                                    accept="image/*"
                                    maxFiles={1}
                                    maxSizeInBytes={5 * 1024 * 1024}
                                    previewSize="md"
                                    previewUrl="/static/images/profile.png"
                                />
                            </FormControl.Container>
                        </div>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 35. ApiForm & ApiFormControl */}
                <section className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">
                        35. ApiForm, ApiFormControl & ApiFormButton
                    </h2>
                    <div className="bg-white p-6 rounded-xl border border-stone-200 space-y-4">
                        <p className="text-sm text-gray-600">
                            Native form wrapper binding server responses,
                            automatic CSRF/action handling, validation messages,
                            and loading submit button.
                        </p>
                        <ApiForm
                            apiForm={apiForm}
                            url="/api/example"
                            onSubmit={async (e) => {
                                e.preventDefault()
                                alert('Form submitted via ApiForm!')
                            }}
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <ApiFormControl
                                    inputName={
                                        RequestBodyKeyEnum.Auth_User_Login_Email
                                    }
                                    label="Account Email"
                                    required
                                    apiFormResponse={null}
                                    helperText="We will never share your email."
                                >
                                    <TextInput
                                        type="email"
                                        placeholder="user@example.com"
                                    />
                                </ApiFormControl>

                                <ApiFormControl
                                    inputName={
                                        RequestBodyKeyEnum.Auth_User_Login_Password
                                    }
                                    label="Account Password"
                                    required
                                    apiFormResponse={null}
                                >
                                    <TextInput
                                        type="password"
                                        placeholder="••••••••"
                                    />
                                </ApiFormControl>
                            </div>

                            <div className="mt-4 flex justify-end">
                                <ApiFormButton
                                    apiForm={apiForm}
                                    color="primary"
                                >
                                    Save Changes
                                </ApiFormButton>
                            </div>
                        </ApiForm>
                    </div>
                </section>

                <SideFormSection.Divider />

                {/* 36. Themed Components (useBookingFormThemeCssVariables) */}
                <section className="space-y-4">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">
                            36. Themed Components
                            (useBookingFormThemeCssVariables)
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                            Components with{' '}
                            <code className="bg-stone-100 px-1 py-0.5 rounded text-xs font-mono">
                                useBookingFormThemeCssVariables
                            </code>{' '}
                            dynamically inherit theme colors from CSS custom
                            properties on their container. Below are 3 distinct
                            scoped themes rendered side-by-side.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Theme 1: Forest Emerald */}
                        <div
                            className="bg-white p-5 rounded-xl border border-stone-200 shadow-xs space-y-4"
                            style={
                                {
                                    '--booking-form-theme-main-background-color':
                                        '#059669',
                                    '--booking-form-theme-main-background-color-90':
                                        '#047857',
                                    '--booking-form-theme-main-text-shade':
                                        '#ffffff',
                                    '--booking-form-theme-main-background-shade':
                                        '#ecfdf5',
                                    '--booking-form-theme-main-text-color':
                                        '#065f46',
                                } as CSSProperties
                            }
                        >
                            <div className="flex items-center justify-between border-b border-stone-100 pb-3">
                                <span className="font-bold text-gray-800 text-sm">
                                    Emerald Theme
                                </span>
                                <span
                                    className="w-4 h-4 rounded-full"
                                    style={{ backgroundColor: '#059669' }}
                                />
                            </div>

                            <div className="space-y-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Themed Text Input
                                    </label>
                                    <TextInput
                                        useBookingFormThemeCssVariables={true}
                                        placeholder="Focus to see emerald ring..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Themed Select
                                    </label>
                                    <SelectInput
                                        useBookingFormThemeCssVariables={true}
                                    >
                                        <option value="1">
                                            30 Minute Consultation
                                        </option>
                                        <option value="2">
                                            60 Minute Deep Dive
                                        </option>
                                    </SelectInput>
                                </div>

                                <div className="space-y-2 pt-1">
                                    <CheckboxInput
                                        label="Emerald Checkbox"
                                        useBookingFormThemeCssVariables={true}
                                        defaultChecked={true}
                                    />
                                    <div className="space-y-1 pt-1">
                                        <RadioInput
                                            label="Emerald 1"
                                            name="demo-theme-emerald-radio"
                                            value="emerald-1"
                                            useBookingFormThemeCssVariables={
                                                true
                                            }
                                            defaultChecked={true}
                                        />
                                        <RadioInput
                                            label="Emerald 2"
                                            name="demo-theme-emerald-radio"
                                            value="emerald-2"
                                            useBookingFormThemeCssVariables={
                                                true
                                            }
                                        />
                                        <RadioInput
                                            label="Emerald 3"
                                            name="demo-theme-emerald-radio"
                                            value="emerald-3"
                                            useBookingFormThemeCssVariables={
                                                true
                                            }
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Themed Textarea
                                    </label>
                                    <TextareaInput
                                        useBookingFormThemeCssVariables={true}
                                        rows={2}
                                        placeholder="Themed focus border..."
                                    />
                                </div>

                                <Button
                                    useBookingFormThemeCssVariables={true}
                                    className="w-full"
                                >
                                    Book with Emerald
                                </Button>
                            </div>
                        </div>

                        {/* Theme 2: Ocean Indigo */}
                        <div
                            className="bg-white p-5 rounded-xl border border-stone-200 shadow-xs space-y-4"
                            style={
                                {
                                    '--booking-form-theme-main-background-color':
                                        '#4f46e5',
                                    '--booking-form-theme-main-background-color-90':
                                        '#4338ca',
                                    '--booking-form-theme-main-text-shade':
                                        '#ffffff',
                                    '--booking-form-theme-main-background-shade':
                                        '#eef2ff',
                                    '--booking-form-theme-main-text-color':
                                        '#3730a3',
                                } as CSSProperties
                            }
                        >
                            <div className="flex items-center justify-between border-b border-stone-100 pb-3">
                                <span className="font-bold text-gray-800 text-sm">
                                    Indigo Theme
                                </span>
                                <span
                                    className="w-4 h-4 rounded-full"
                                    style={{ backgroundColor: '#4f46e5' }}
                                />
                            </div>

                            <div className="space-y-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Themed Text Input
                                    </label>
                                    <TextInput
                                        useBookingFormThemeCssVariables={true}
                                        placeholder="Focus to see indigo ring..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Themed Select
                                    </label>
                                    <SelectInput
                                        useBookingFormThemeCssVariables={true}
                                    >
                                        <option value="1">
                                            30 Minute Consultation
                                        </option>
                                        <option value="2">
                                            60 Minute Deep Dive
                                        </option>
                                    </SelectInput>
                                </div>

                                <div className="space-y-2 pt-1">
                                    <CheckboxInput
                                        label="Indigo Checkbox"
                                        useBookingFormThemeCssVariables={true}
                                        defaultChecked={true}
                                    />
                                    <div className="space-y-1 pt-1">
                                        <RadioInput
                                            label="Indigo 1"
                                            name="demo-theme-indigo-radio"
                                            value="indigo-1"
                                            useBookingFormThemeCssVariables={
                                                true
                                            }
                                            defaultChecked={true}
                                        />
                                        <RadioInput
                                            label="Indigo 2"
                                            name="demo-theme-indigo-radio"
                                            value="indigo-2"
                                            useBookingFormThemeCssVariables={
                                                true
                                            }
                                        />
                                        <RadioInput
                                            label="Indigo 3"
                                            name="demo-theme-indigo-radio"
                                            value="indigo-3"
                                            useBookingFormThemeCssVariables={
                                                true
                                            }
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Themed Textarea
                                    </label>
                                    <TextareaInput
                                        useBookingFormThemeCssVariables={true}
                                        rows={2}
                                        placeholder="Themed focus border..."
                                    />
                                </div>

                                <Button
                                    useBookingFormThemeCssVariables={true}
                                    className="w-full"
                                >
                                    Book with Indigo
                                </Button>
                            </div>
                        </div>

                        {/* Theme 3: Sunset Amber */}
                        <div
                            className="bg-white p-5 rounded-xl border border-stone-200 shadow-xs space-y-4"
                            style={
                                {
                                    '--booking-form-theme-main-background-color':
                                        '#d97706',
                                    '--booking-form-theme-main-background-color-90':
                                        '#b45309',
                                    '--booking-form-theme-main-text-shade':
                                        '#ffffff',
                                    '--booking-form-theme-main-background-shade':
                                        '#fffbeb',
                                    '--booking-form-theme-main-text-color':
                                        '#92400e',
                                } as CSSProperties
                            }
                        >
                            <div className="flex items-center justify-between border-b border-stone-100 pb-3">
                                <span className="font-bold text-gray-800 text-sm">
                                    Amber Theme
                                </span>
                                <span
                                    className="w-4 h-4 rounded-full"
                                    style={{ backgroundColor: '#d97706' }}
                                />
                            </div>

                            <div className="space-y-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Themed Text Input
                                    </label>
                                    <TextInput
                                        useBookingFormThemeCssVariables={true}
                                        placeholder="Focus to see amber ring..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Themed Select
                                    </label>
                                    <SelectInput
                                        useBookingFormThemeCssVariables={true}
                                    >
                                        <option value="1">
                                            30 Minute Consultation
                                        </option>
                                        <option value="2">
                                            60 Minute Deep Dive
                                        </option>
                                    </SelectInput>
                                </div>

                                <div className="space-y-2 pt-1">
                                    <CheckboxInput
                                        label="Amber Checkbox"
                                        useBookingFormThemeCssVariables={true}
                                        defaultChecked={true}
                                    />
                                    <div className="space-y-1 pt-1">
                                        <RadioInput
                                            label="Amber 1"
                                            name="demo-theme-amber-radio"
                                            value="amber-1"
                                            useBookingFormThemeCssVariables={
                                                true
                                            }
                                            defaultChecked={true}
                                        />
                                        <RadioInput
                                            label="Amber 2"
                                            name="demo-theme-amber-radio"
                                            value="amber-2"
                                            useBookingFormThemeCssVariables={
                                                true
                                            }
                                        />
                                        <RadioInput
                                            label="Amber 3"
                                            name="demo-theme-amber-radio"
                                            value="amber-3"
                                            useBookingFormThemeCssVariables={
                                                true
                                            }
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Themed Textarea
                                    </label>
                                    <TextareaInput
                                        useBookingFormThemeCssVariables={true}
                                        rows={2}
                                        placeholder="Themed focus border..."
                                    />
                                </div>

                                <Button
                                    useBookingFormThemeCssVariables={true}
                                    className="w-full"
                                >
                                    Book with Amber
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    )
}

export default KitchenSinkPage
